import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, updateUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('success');
      setMessage('Payment is being processed. Your credits will be added shortly.');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/payments/stripe/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your credits have been added.');
        // Refresh user data
        const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateUser(userResponse.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setMessage('Error verifying payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted/30">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          {status === 'loading' && (
            <Loader2 className="w-16 h-16 mx-auto text-lime-500 animate-spin" />
          )}
          {status === 'success' && (
            <CheckCircle2 className="w-16 h-16 mx-auto text-lime-500" />
          )}
          <CardTitle className="font-heading text-2xl mt-4">
            {status === 'loading' ? 'Processing Payment...' : 
             status === 'success' ? 'Payment Successful!' : 'Payment Issue'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{message || 'Verifying your payment...'}</p>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="bg-lime-500 hover:bg-lime-600 text-white"
            data-testid="go-to-dashboard"
          >
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
